import {
  popover as lluiPopover,
  type PopoverMsg,
  type PopoverParts,
  type PopoverState,
} from '@llui/components/popover';
import { div, type Mountable, type Renderable, type Send, type Signal } from '@llui/dom';

/**
 * `<Popover>` — a controlled, conditionally-mounted adapter over LLui's
 * standard popover.
 *
 * LLui owns portalling, collision-aware floating placement, Escape/outside
 * dismissal, layer stacking, and focus restoration. The host owns whether the
 * adapter is mounted and the trigger gesture (the calendar field deliberately
 * opens on pointer-down to survive blur-driven row remounts).
 */

export interface PopoverLabels {
  close: string;
}

export type PopoverPlacement = NonNullable<
  Parameters<typeof lluiPopover.overlay>[0]['placement']
>;

export interface PopoverFloating {
  /** Preferred placement; LLui flips and shifts it within the viewport. */
  placement?: PopoverPlacement;
  /** Gap between trigger and panel, in px. Defaults to 4. */
  offset?: number;
}

export interface PopoverOptions {
  /** Host-owned open state. */
  open: Signal<boolean>;
  /** Called when LLui dismisses the popover. */
  onDismiss: () => void;
  /** Stable id of the already-mounted trigger used for positioning/focus return. */
  triggerId: string;
  /** Optional CSS class for app-specific panel layout. */
  panelClass?: string;
  /** Optional id on the content panel. */
  panelId?: string;
  /** Forwarded `data-testid` on the content panel. */
  testid?: string;
  /** Localised labels. */
  labels: PopoverLabels;
  /** Collision-aware placement options. */
  floating?: PopoverFloating;
  /** Children rendered inside the content panel. */
  children: Renderable;
}

let popoverSeq = 0;

export function popover(opts: PopoverOptions): Mountable {
  const id = `dl-popover-${popoverSeq++}`;
  const panelClass = `dl-popover-panel${opts.panelClass ? ` ${opts.panelClass}` : ''}`;
  const state = opts.open.map(
    (open): PopoverState => ({
      open,
      status: open ? 'open' : 'closed',
      skipAnimations: true,
    }),
  );
  const send: Send<PopoverMsg> = (msg) => {
    if (
      msg.type === 'close' ||
      msg.type === 'toggle' ||
      (msg.type === 'setOpen' && !msg.open)
    ) {
      opts.onDismiss();
    }
  };
  const connected = lluiPopover.connect(state, send, {
    id,
    closeLabel: opts.labels.close,
  });
  const parts: PopoverParts = {
    ...connected,
    trigger: { ...connected.trigger, id: opts.triggerId },
    content: {
      ...connected.content,
      id: opts.panelId ?? connected.content.id,
      'aria-labelledby': opts.triggerId,
    },
  };

  return div({ class: 'dl-popover-boundary' }, [
    lluiPopover.overlay({
      state,
      send,
      parts,
      positionerClass: 'dl-popover',
      placement: opts.floating?.placement ?? 'bottom-start',
      offset: opts.floating?.offset ?? 4,
      content: () => [
        div(
          {
            ...parts.content,
            class: panelClass,
            'data-testid': opts.testid,
          },
          opts.children,
        ),
      ],
    }),
  ]);
}
